import { Button, Tag } from "../../../../../../../shared/frontend/ui";
import { formatCheckInEventMessage } from "../../../app/checkin/checkInEventMessage";
import { CheckInActionCode } from "../../../app/checkin/checkInActionCode";
import { formatCheckInSource } from "../../../app/checkin/checkInSource";
import type { I18nContextValue } from "../../../app/i18n/useI18n";
import { formatDateTimeIso } from "../../../app/units/format";
import type { UnitsSettings } from "../../../app/units/units";
import { ManagedTableList } from "../../../../../../../shared/frontend/table/ManagedTableList";

/** Numerische ReaderAction des Backends. */
const READER_ACTION_CODE_OPEN_DOOR = CheckInActionCode.OpenDoor;
const READER_ACTION_CODE_UNLOCK_DEVICE = CheckInActionCode.UnlockDevice;
const READER_ACTION_CODE_DEDUCT_POINTS = CheckInActionCode.DeductPoints;

export type PersonReaderActionEvent = {
  id: string;
  createdAt: string;
  action: number;
  source?: string | number | null;
  studioId?: string | null;
  message?: string | null;
  allowed: boolean;
  walletId?: string | null;
  walletLabel?: string | null;
};

type PersonReaderActionsTableProps = {
  events: PersonReaderActionEvent[];
  allowedStudios: Array<{ id: string; name?: string | null }>;
  canReadFinance: boolean;
  text: I18nContextValue["text"];
  units: UnitsSettings;
  onOpenWallets: () => void;
};

function readerActionLabel(action: number, text: I18nContextValue["text"]): string {
  if (action === READER_ACTION_CODE_OPEN_DOOR) return text("Open door");
  if (action === READER_ACTION_CODE_UNLOCK_DEVICE) return text("Unlock device");
  if (action === READER_ACTION_CODE_DEDUCT_POINTS) return text("Deduct points");
  return text("—");
}

export function PersonReaderActionsTable({
  events,
  allowedStudios,
  canReadFinance,
  text,
  units,
  onOpenWallets
}: PersonReaderActionsTableProps) {
  return (
    <ManagedTableList
      storageKey="m1member.person-reader-actions-table"
      columns={[
        {
          title: text("Date"),
          dataIndex: "createdAt",
          key: "createdAt",
          width: 160,
          render: (v: string) =>
            v
              ? formatDateTimeIso(String(v), units, {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit"
                })
              : text("—")
        },
        {
          title: text("Action"),
          dataIndex: "action",
          key: "action",
          width: 140,
          render: (a: number) => readerActionLabel(Number(a), text)
        },
        {
          title: text("Source"),
          dataIndex: "source",
          key: "source",
          width: 150,
          render: (source: string | number | null | undefined) => formatCheckInSource(source, text)
        },
        {
          title: text("Studio"),
          dataIndex: "studioId",
          key: "studioId",
          width: 160,
          render: (sid: string | null | undefined) => {
            if (!sid) return text("—");
            const studio = allowedStudios.find((s) => String(s.id) === String(sid));
            return studio?.name || sid;
          }
        },
        {
          title: text("Wallet Product"),
          key: "wallet",
          ellipsis: true,
          render: (_: unknown, row: PersonReaderActionEvent) => {
            const label = row.walletLabel?.trim() || "";
            if (!row.walletId || !canReadFinance) {
              return label || text("—");
            }
            return (
              <Button
                type="link"
                size="small"
                style={{ padding: 0, height: "auto", whiteSpace: "normal", textAlign: "left" }}
                onClick={onOpenWallets}
              >
                {label || text("Go to wallets tab")}
              </Button>
            );
          }
        },
        {
          title: text("Message"),
          dataIndex: "message",
          key: "message",
          ellipsis: true,
          render: (m: string | null | undefined) => formatCheckInEventMessage(m, text)
        },
        {
          title: text("Status"),
          dataIndex: "allowed",
          key: "allowed",
          width: 100,
          render: (ok: boolean) =>
            ok ? <Tag color="green">{text("Allowed")}</Tag> : <Tag color="red">{text("Denied")}</Tag>
        }
      ]}
      tr={text}
      pagination={false}
      rowKey="id"
      dataSource={events}
      emptyText={text("Person overview no reader actions yet")}
    />
  );
}
